import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import type { JuiImageEntry, JuiImageRejection } from '@jump-tech-frontend/core-components/components';
import {
  CameraI18nKeys,
  isStoredImageUrl,
  OcrImageCropper,
  OcrService,
  PhotoCaptureTrigger,
  pickOcrReading,
  StoredImageLoader
} from '@jump-tech-frontend/image-analysis';
import { TRANSLATIONS_PROVIDER } from '../../translations.token';
import { CAMERA_KEYS, REJECTION_KEYS } from '../capture-wording';
import { FormUpdate } from '../question.model';
import { OcrCaptureQuestionParams, OcrCaptureVm, OcrImageValue } from './ocr-capture.model';

interface OcrImage {
  id: string;
  answer: string;
  displaySrc: string;
  displayState: JuiImageEntry['state'];
}

function firstImageOf(value: OcrImageValue | undefined): string | null {
  return (Array.isArray(value) ? value[0] : value) || null;
}

function withoutSpaces(text: string | undefined): string {
  return text?.replace(/ /g, '') || '';
}

@Injectable()
export class OcrCapturePresenter {
  private readonly ocr = inject(OcrService);
  private readonly cropper = inject(OcrImageCropper);
  private readonly storedImages = inject(StoredImageLoader);
  private readonly photoCapture = inject(PhotoCaptureTrigger);
  private readonly translations = inject(TRANSLATIONS_PROVIDER);

  private readonly params = signal<OcrCaptureQuestionParams>({ key: '' });
  private readonly image = signal<OcrImage | null>(null);
  private readonly reading = signal(false);
  private readonly rejection = signal<string | null>(null);
  private nextStoredId = 0;
  private emit: (update: FormUpdate) => void = () => undefined;

  public readonly vm = computed<OcrCaptureVm>(() => {
    const image = this.image();
    return {
      key: this.params().key,
      images: image ? [{ id: image.id, src: image.displaySrc, state: image.displayState }] : [],
      examples: image ? [] : (this.params().examples ?? []),
      reading: this.reading(),
      rejection: this.rejection()
    };
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.releaseImage());
  }

  public onFormChange(emit: (update: FormUpdate) => void): void {
    this.emit = emit;
  }

  public load(params: OcrCaptureQuestionParams): void {
    this.params.set(params);
    if (this.reading()) {
      return;
    }

    const source = firstImageOf(params.value);
    if (source === this.image()?.answer) {
      return;
    }

    this.releaseImage();
    this.image.set(source ? this.storedImage(source) : null);
  }

  public async imagesChanged(entries: JuiImageEntry[]): Promise<void> {
    this.rejection.set(null);
    const added = entries.find(entry => entry.id !== this.image()?.id);
    if (!entries.length) {
      this.releaseImage();
      this.image.set(null);
      this.write(this.params().targetOcrField, null);
      return;
    }
    if (added) {
      this.image.set({ id: added.id, answer: added.src, displaySrc: added.src, displayState: 'ready' });
      await this.read(added);
    }
  }

  public rejected(rejection: JuiImageRejection): void {
    const key = REJECTION_KEYS[rejection.reason];
    this.rejection.set(key ? this.translations.getTranslation(key) : null);
  }

  public offersCamera(): boolean {
    return this.photoCapture.isAvailable();
  }

  public captureWithCamera(openPicker: () => void): Promise<string | null> {
    return this.photoCapture.capture(openPicker, this.cameraI18n());
  }

  private async read(entry: JuiImageEntry): Promise<void> {
    const { targetOcr, targetOcrField, patterns } = this.params();
    this.reading.set(true);
    this.write(targetOcr, '');
    try {
      const reading = pickOcrReading(await this.ocr.recognise(entry.src), patterns);
      this.write(targetOcr, withoutSpaces(reading.DetectedText));
      const polygon = reading.Geometry?.Polygon;
      if (!polygon || !targetOcrField) {
        this.drop(entry.id);
        return;
      }
      const cropped = await this.cropper.crop(entry.src, polygon);
      this.image.set({ id: entry.id, answer: cropped, displaySrc: cropped, displayState: 'ready' });
      this.write(targetOcrField, [cropped]);
    } catch {
      this.drop(entry.id);
    } finally {
      this.reading.set(false);
    }
  }

  private drop(id: string): void {
    if (this.image()?.id === id) {
      this.image.set(null);
    }
  }

  private write(key: string | undefined, value: unknown): void {
    if (key) {
      this.emit({ key, value });
    }
  }

  private storedImage(source: string): OcrImage {
    this.nextStoredId += 1;
    const id = `stored-${this.nextStoredId}`;
    if (!isStoredImageUrl(source)) {
      return { id, answer: source, displaySrc: source, displayState: 'ready' };
    }
    void this.loadStored(id, source);
    return { id, answer: source, displaySrc: '', displayState: 'loading' };
  }

  private async loadStored(id: string, source: string): Promise<void> {
    try {
      const displaySrc = await this.storedImages.displayUrlFor(source);
      this.update(id, { displaySrc, displayState: 'ready' });
    } catch {
      this.update(id, { displayState: 'error' });
    }
  }

  private update(id: string, changes: Partial<OcrImage>): void {
    const image = this.image();
    if (image?.id === id) {
      this.image.set({ ...image, ...changes });
    }
  }

  private releaseImage(): void {
    const image = this.image();
    if (image) {
      this.storedImages.release(image.displaySrc);
    }
  }

  private cameraI18n(): CameraI18nKeys {
    return Object.fromEntries(CAMERA_KEYS.map(key => [key, this.translations.getTranslation(key)])) as CameraI18nKeys;
  }
}
